import { useMemo } from "react";
import {
  ConnectorType,
  UserExternalAuthStatus,
  useGetUserConnectionsQuery,
  useGetConnectorCatalogQuery,
  useGetEnabledConnectorsQuery,
  ConnectorInstanceStatus,
  ConnectorCapability,
} from "@axio/graphql/generated/graphql";

import {
  PickerSource,
  PickerSourceStatus,
  PickerContext,
} from "../../components/common/general/ContentPicker/ContentPicker.types";
import { useAuth } from "../useAuth";
import { connectorAllowsUserRole } from "../../utils/integrations/roleAccess";

// ---------------------------------------------------------------------------
// Map ConnectorType → display label & icon key
// ---------------------------------------------------------------------------

export const CONNECTOR_META: Record<ConnectorType, { label: string; iconKey: string }> = {
  [ConnectorType.GoogleDrive]: { label: "Google Drive", iconKey: "google-drive" },
  [ConnectorType.OneDrive]: { label: "OneDrive", iconKey: "microsoft-onedrive" },
  [ConnectorType.Kaltura]: { label: "Kaltura", iconKey: "kaltura" },
  [ConnectorType.LegacyConnector]: { label: "External Source", iconKey: "" },
};

function authStatusToPickerStatus(status: UserExternalAuthStatus): PickerSourceStatus {
  switch (status) {
    case UserExternalAuthStatus.Active:
      return "connected";
    case UserExternalAuthStatus.ReauthRequired:
      return "reauth_required";
    case UserExternalAuthStatus.Error:
      return "error";
    case UserExternalAuthStatus.Revoked:
    default:
      return "not_connected";
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseContentPickerSourcesResult {
  sources: PickerSource[];
  loading: boolean;
  error: boolean;
  refetchSources: () => Promise<any>;
}

export function useContentPickerSources(context?: PickerContext): UseContentPickerSourcesResult {
  const { userInfo } = useAuth();

  const {
    data: connectionsData,
    loading: connectionsLoading,
    error: connectionsError,
    refetch: refetchConnections,
  } = useGetUserConnectionsQuery({
    fetchPolicy: "network-only",
  });
  //  fetch the catalog and enabled connectors separately from the user connections to ensure we have the most up-to-date information on available connectors and their auth URLs.
  const {
    data: catalogData,
    loading: catalogLoading,
    error: catalogError,
  } = useGetConnectorCatalogQuery({
    fetchPolicy: "network-only",
    variables: {
      returnUrl: typeof window !== "undefined" ? `${window.location.origin}/oauth-callback` : undefined,
    },
  });

  const { data: enabledConnectorsData } = useGetEnabledConnectorsQuery({
    variables: {
      filter: {
        status: ConnectorInstanceStatus.Active,
        role: userInfo.userRole,
      },
    },
  });

  const sources = useMemo<PickerSource[]>(() => {
    const userConnections = connectionsData?.getUserConnections?.data ?? [];
    const catalogConnectors = catalogData?.getConnectorCatalog?.data ?? [];

    const allowedEnabledConnectors = (enabledConnectorsData?.getEnabledConnectors?.data ?? []).filter(
      (connector) => connector !== null
    );
    const allowedConnectorTypes = new Set(
      allowedEnabledConnectors
        .filter((connector) => connectorAllowsUserRole(connector.enabledRoles, userInfo.userRole))
        .map((connector) => connector.connectorType)
    );

    // Build a lookup: ConnectorType → authorizationUrl from the catalog.
    // The backend bakes the returnUrl into this URL.
    const authUrlMap = new Map<ConnectorType, string | undefined>();
    for (const entry of catalogConnectors) {
      if (entry?.connectorType && entry.savedConfiguration?.authorizationUrl) {
        authUrlMap.set(entry.connectorType, entry.savedConfiguration.authorizationUrl);
      }
    }

    // Build a lookup: ConnectorType → ConnectorCapability[]
    const capabilitiesMap = new Map<ConnectorType, ConnectorCapability[]>();
    for (const entry of catalogConnectors) {
      if (entry?.connectorType && entry?.capabilities) {
        capabilitiesMap.set(entry.connectorType, entry.capabilities);
      }
    }

    // Build a map from ConnectorType → PickerSource for personal connections
    const personalMap = new Map<ConnectorType, PickerSource>();

    for (const conn of userConnections) {
      if (!conn?.connectorType) continue;
      if (conn.status === UserExternalAuthStatus.Revoked) continue;

      const ct = conn.connectorType;
      // Filter by allowed connector types based on role
      if (!allowedConnectorTypes.has(ct)) continue;

      const meta = CONNECTOR_META[ct];
      if (!meta) continue;

      personalMap.set(ct, {
        connectorType: ct,
        label: meta.label,
        iconKey: meta.iconKey,
        status: authStatusToPickerStatus(conn.status),
        authUrl: authUrlMap.get(ct),
      });
    }

    // Add catalog connectors the user hasn't personally connected yet
    for (const entry of catalogConnectors) {
      if (!entry?.connectorType) continue;
      const ct = entry.connectorType;
      if (personalMap.has(ct)) continue;

      // Filter by allowed connector types based on role
      if (!allowedConnectorTypes.has(ct)) continue;

      const meta = CONNECTOR_META[ct];
      if (!meta) continue;

      personalMap.set(ct, {
        connectorType: ct,
        label: meta.label,
        iconKey: meta.iconKey,
        status: "not_connected",
        authUrl: entry.savedConfiguration?.authorizationUrl ?? undefined,
      });
    }

    // Filter sources by required capabilities if context provided
    let sources = Array.from(personalMap.values());
    if (context?.requiredCapabilities?.length) {
      sources = sources.filter((source) => {
        const caps = capabilitiesMap.get(source.connectorType);
        if (!caps) return false;
        return context.requiredCapabilities!.every((req) => caps.includes(req));
      });
    }

    return sources.sort((a, b) => {
      return a.label.localeCompare(b.label);
    });
  }, [connectionsData, catalogData, enabledConnectorsData, userInfo.userRole, context]);

  return {
    sources,
    loading: connectionsLoading || catalogLoading,
    error: !!(connectionsError || catalogError),
    refetchSources: refetchConnections,
  };
}
